import React, { useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import styled from "styled-components";

import { ISODate } from "../../types/common";
import { formatToDate } from "../../utils/common";

import { Badge } from "./core";
import { BadgeType } from "./core/Badge";
import { HistoryIcon } from "./core/icons";

export type StatusEntry = {
    role:
        | "Service Provider"
        | "Claim Approvers"
        | "Project Officer"
        | "Architect"
        | "Finance Department"
        | "Payment Order Approvers"; // TODO change "Approver" to Payment...? TBD
    date: ISODate | null;
    status:
        | "CTA"
        | "Created"
        | "Submitted"
        | "Approved"
        | "Rejected"
        | "Pending"
        | "Negotiated"
        | "Discarded"
        | "Payment Approved"
        | "Payment Created"
        | "Payment Rejected";
    badgeType: BadgeType | null;
};

type StatusDetailProps = {
    className?: string;
    statusEntries: StatusEntry[];
    children: React.ReactNode;
};

const StatusDetail = ({
    className,
    statusEntries,
    children,
}: StatusDetailProps): JSX.Element => {
    const [isOpened, setIsOpened] = useState(false);
    return (
        <Container
            className={className}
            hoverPointer={statusEntries.length > 0}
            onMouseEnter={() => statusEntries.length > 0 && setIsOpened(true)}
            onMouseLeave={() => statusEntries.length > 0 && setIsOpened(false)}
            onKeyUp={() => statusEntries.length > 0 && setIsOpened(true)}
            onKeyDown={() => statusEntries.length > 0 && setIsOpened(false)}
        >
            {children}
            {isOpened && (
                <StyledCard className="position-absolute end-0 rounded-2 bg-light">
                    <Card.Body className="p-4">
                        {statusEntries.map((it, index) => (
                            <StatusDetailEntry key={index} statusEntry={it} />
                        ))}
                    </Card.Body>
                </StyledCard>
            )}
        </Container>
    );
};

type StatusDetailEntryProps = {
    statusEntry: StatusEntry;
};

export const StatusDetailEntry = ({
    statusEntry,
}: StatusDetailEntryProps): JSX.Element => {
    return (
        <StyledRow
            className="align-items-center"
            $isPending={statusEntry.status === "CTA"}
        >
            <Col>
                <p className="mb-0 fw-bold">{statusEntry.role}</p>
                <p className="mb-0 fs-7">
                    {statusEntry.date ? formatToDate(statusEntry.date) : "-"}
                </p>
            </Col>
            <Col className="text-end">
                {statusEntry.status === "CTA" && <HistoryIcon />}
                {statusEntry.badgeType && statusEntry.status !== "CTA" && (
                    <Badge type={statusEntry.badgeType}>
                        {statusEntry.status}
                    </Badge>
                )}
            </Col>
        </StyledRow>
    );
};

const Container = styled.div<{ hoverPointer?: boolean }>`
    position: relative;
    ${(props) =>
        props.hoverPointer &&
        `
        cursor: pointer;
    `}
`;

const StyledCard = styled(Card)`
    top: 30px;
    width: 250px;
    box-shadow: 0 8px 24px rgba(69, 90, 99, 0.24);
    border: none;
    z-index: 1;
`;

const StyledRow = styled(Row)<{ $isPending?: boolean }>`
    color: ${(props) =>
        props.$isPending ? "var(--bs-placeholder-grey)" : "var(--bs-dark)"};

    &:not(:last-child) {
        margin-bottom: 1.5rem;
    }
`;

export default StatusDetail;
